import { getLtv } from "shared/getLtv";
import { getMonthlyInstalment } from "shared/getMonthlyInstalment";
import { presentValue } from "shared/presentValue";
import { RefinancingOutcome } from "web_myplann/Calculators/Types/RefinancingOutcome";
import type { RefinancingPlan } from "web_myplann/Calculators/Types/RefinancingPlan";
import { findBankRates } from "web_myplann/Calculators/Utils/findBankRates";
import { findLowestBankRate } from "web_myplann/Calculators/Utils/findLowestBankRate";
import type { MortgageRateResponseDTO } from "web_myplann/Infrastructure/Api/Api";

type Params = {
	propertyPrice: number;
	monthlyPayment: number;
	interest: number;
	years: number;
	paidYears: number;
	rates: MortgageRateResponseDTO[];
};

export const getRefinancingPlan = ({
	propertyPrice,
	monthlyPayment,
	interest,
	years,
	paidYears,
	rates,
}: Params): RefinancingPlan => {
	const remainingYears = years - paidYears;
	const leftToPay =
		interest > 0 && remainingYears > 0
			? Math.round(
					Math.abs(
						presentValue(interest / 1200, remainingYears * 12, monthlyPayment),
					),
				)
			: 0;
	const ltv = getLtv(leftToPay, propertyPrice);
	const bankRates = findBankRates(ltv, leftToPay, remainingYears, rates);
	const rate = leftToPay > 0 ? findLowestBankRate(bankRates) : undefined;
	const newMonthlyPayment =
		rate === undefined
			? 0
			: getMonthlyInstalment(rate, remainingYears, leftToPay);
	const saving =
		rate !== undefined && rate < interest && newMonthlyPayment > 0
			? Math.max(0, Math.round(monthlyPayment - newMonthlyPayment))
			: 0;
	const outcome =
		saving > 0
			? RefinancingOutcome.Saving
			: rate !== undefined && rate > interest
				? RefinancingOutcome.Better
				: RefinancingOutcome.Same;

	return {
		remainingYears,
		leftToPay,
		ltv,
		bankRates,
		rate,
		newMonthlyPayment,
		saving,
		outcome,
	};
};
